/**
 * Jun-2026 (User-Wunsch) — König-Integration (bidirektional).
 * Bestands-/Standort-Sync aus den König-APIs (Import) + Hinweis auf die
 * bereitgestellten EXPOSE-Endpunkte. Läuft im Mock-Modus, bis echte Keys
 * hinterlegt sind. Manueller Import per Knopfdruck + täglicher Cron-Sync.
 */
import { useEffect, useState, useCallback } from "react";
import { Plug, RefreshCw, Download, CheckCircle2, AlertTriangle, Clock, Building2, Bike } from "lucide-react";
import { Button } from "../ui/button";
import { Badge } from "../ui/badge";
import { CollapsibleCard } from "./_shared";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast } from "sonner";

const fmtDate = (iso) => {
    if (!iso) return "—";
    try { return new Date(iso).toLocaleString("de-DE"); } catch { return iso; }
};

export function KoenigIntegrationSection({ currentUser }) {
    void currentUser;
    const [status, setStatus] = useState(null);
    const [runs, setRuns] = useState([]);
    const [busy, setBusy] = useState("");

    const load = useCallback(async () => {
        try {
            const [s, r] = await Promise.all([
                api.get("/integrations/koenig/status"),
                api.get("/integrations/koenig/runs", { params: { limit: 8 } }),
            ]);
            setStatus(s.data);
            setRuns(r.data?.items || []);
        } catch { /* ignore */ }
    }, []);

    useEffect(() => { load(); }, [load]);

    const runImport = async (kind) => {
        setBusy(kind);
        try {
            const url = kind === "vehicles" ? "/integrations/koenig/import/vehicles" : "/integrations/koenig/import/branches";
            const body = kind === "branches" ? { apply_hours: true } : undefined;
            const { data } = await api.post(url, body);
            const hoursNote = (kind === "branches" && data.applied_hours) ? " · Öffnungszeiten übernommen" : "";
            toast.success(`${kind === "vehicles" ? "Fahrzeuge" : "Standorte"}: ${data.created} neu, ${data.updated} aktualisiert${data.mock_used ? " (Demo-Daten)" : ""}${hoursNote}`);
            load();
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Import fehlgeschlagen");
        } finally { setBusy(""); }
    };

    const expose = (path) => `${process.env.REACT_APP_BACKEND_URL}/api/integrations/koenig/${path}`;

    return (
        <CollapsibleCard
            icon={Plug}
            iconClass="text-[#7C3AED]"
            title="Live-Bestandssync"
            hint="Bestands- & Standort-Sync über die Live-API. Ohne Zugangsdaten werden Demo-Daten verwendet."
            badge={status && (status.mock_mode
                ? <Badge className="bg-amber-100 text-amber-800 border-amber-200" data-testid="koenig-mode-badge">Demo-Modus (keine Keys)</Badge>
                : <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200" data-testid="koenig-mode-badge">Live verbunden</Badge>)}
            testId="koenig-integration-section"
        >
            {/* Import-Aktionen */}
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
                <div className="border border-gray-200 rounded-sm p-4" data-testid="koenig-vehicles-card">
                    <div className="flex items-center gap-2 mb-2"><Bike className="w-4 h-4 text-[#111827]" /><span className="font-bold text-sm">Fahrzeuge importieren</span></div>
                    <p className="text-xs text-gray-500 mb-3">{status?.imported_vehicles ?? 0} importierte Fahrzeuge im Bestand.</p>
                    <Button onClick={() => runImport("vehicles")} disabled={busy === "vehicles"}
                        className="w-full rounded-sm bg-[#111827] hover:bg-black text-white font-bold gap-2" data-testid="koenig-import-vehicles">
                        {busy === "vehicles" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Jetzt importieren
                    </Button>
                </div>
                <div className="border border-gray-200 rounded-sm p-4" data-testid="koenig-branches-card">
                    <div className="flex items-center gap-2 mb-2"><Building2 className="w-4 h-4 text-[#111827]" /><span className="font-bold text-sm">Standorte importieren</span></div>
                    <p className="text-xs text-gray-500 mb-3">{status?.imported_branches ?? 0} importierte Standorte · Öffnungszeiten werden automatisch übernommen.</p>
                    <Button onClick={() => runImport("branches")} disabled={busy === "branches"}
                        className="w-full rounded-sm bg-[#111827] hover:bg-black text-white font-bold gap-2" data-testid="koenig-import-branches">
                        {busy === "branches" ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />} Jetzt importieren
                    </Button>
                </div>
            </div>

            {/* König-Standort-Hierarchie entfernt (· Jun 2026): Standorte werden
                jetzt zentral unter „Standorte & Filialen" verwaltet. */}

            {/* EXPOSE-Hinweis */}
            <div className="bg-gray-50 border border-gray-200 rounded-sm p-3 mb-5">
                <div className="text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-2">Bereitgestellte API (für die Partner-/Hersteller-Website)</div>
                <div className="space-y-1 text-xs font-mono-num text-gray-600">
                    <div data-testid="koenig-expose-vehicles">GET {expose("vehicles")}</div>
                    <div data-testid="koenig-expose-branches">GET {expose("branches")}</div>
                </div>
                <p className="text-[10px] text-gray-400 mt-2">
                    {status?.expose_protected ? "Geschützt per X-API-Key." : "Aktuell offen (setze KOENIG_EXPOSE_API_KEY zum Schützen)."} Liefert TwoWheels-Daten im Partner-Schema.
                </p>
            </div>

            {/* Verlauf */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] font-bold uppercase tracking-wider text-gray-500">Sync-Verlauf</span>
                    <button onClick={load} className="text-gray-400 hover:text-[#111827]" title="Aktualisieren" data-testid="koenig-refresh"><RefreshCw className="w-3.5 h-3.5" /></button>
                </div>
                {runs.length === 0 ? (
                    <p className="text-xs text-gray-400" data-testid="koenig-runs-empty">Noch keine Synchronisierung durchgeführt.</p>
                ) : (
                    <ul className="divide-y divide-gray-100" data-testid="koenig-runs-list">
                        {runs.map((r) => (
                            <li key={r.id} className="py-2 flex items-center gap-2 text-xs" data-testid={`koenig-run-${r.id}`}>
                                {r.status === "ok" ? <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" /> : <AlertTriangle className="w-4 h-4 text-red-500 shrink-0" />}
                                <span className="font-bold w-20">{r.kind === "vehicles" ? "Fahrzeuge" : "Standorte"}</span>
                                <span className="text-gray-600 flex-1">{r.created} neu · {r.updated} akt.{r.mock_used ? " · Demo" : ""}</span>
                                <span className="text-[10px] uppercase text-gray-400">{r.trigger}</span>
                                <span className="text-gray-400 inline-flex items-center gap-1"><Clock className="w-3 h-3" />{fmtDate(r.started_at)}</span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </CollapsibleCard>
    );
}

export default KoenigIntegrationSection;
